import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { API_URL } from '../config';
import { useT } from '../i18n/useT';

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  userId?: number; // If undefined = current user, if provided = admin changing other user
  userName?: string; // Display name for admin change password
}

interface PasswordErrors {
  [key: string]: string;
}

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({
  open,
  onClose,
  onSuccess,
  userId,
  userName,
}) => {
  const [formData, setFormData] = useState({
    old_password: '',
    new_password: '',
    new_password_confirm: '',
  });
  const [errors, setErrors] = useState<PasswordErrors>({});
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const t = useT();

  const isAdminChanging = userId !== undefined && userId !== null;

  const validatePasswords = (): boolean => {
    const newErrors: PasswordErrors = {};

    // Old password validation (only for self-service)
    if (!isAdminChanging && !formData.old_password) {
      newErrors.old_password = t('auth.changePassword.errors.oldPasswordRequired');
    }

    // New password validation
    if (!formData.new_password) {
      newErrors.new_password = t('auth.changePassword.errors.newPasswordRequired');
    } else if (formData.new_password.length < 8) {
      newErrors.new_password = t('auth.changePassword.errors.tooShort');
    } else if (!/[A-Z]/.test(formData.new_password)) {
      newErrors.new_password = t('auth.changePassword.errors.missingUppercase');
    } else if (!/[a-z]/.test(formData.new_password)) {
      newErrors.new_password = t('auth.changePassword.errors.missingLowercase');
    } else if (!/\d/.test(formData.new_password)) {
      newErrors.new_password = t('auth.changePassword.errors.missingDigit');
    }

    // Confirm password validation
    if (!formData.new_password_confirm) {
      newErrors.new_password_confirm = t('auth.changePassword.errors.confirmRequired');
    } else if (formData.new_password !== formData.new_password_confirm) {
      newErrors.new_password_confirm = t('auth.changePassword.errors.mismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    setFormData({ old_password: '', new_password: '', new_password_confirm: '' });
    setErrors({});
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePasswords()) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        notify(t('auth.changePassword.notifications.authRequired'), { type: 'error' });
        setLoading(false);
        return;
      }

      const endpoint = isAdminChanging
        ? `${API_URL}/auth/users/${userId}/change-password`
        : `${API_URL}/auth/change-password`;

      const payload = {
        old_password: formData.old_password,
        new_password: formData.new_password,
        new_password_confirm: formData.new_password_confirm,
      };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let detail = t('auth.changePassword.notifications.failed');
        try {
          const body = await response.json();
          detail = body?.detail || detail;
        } catch {
          // Ignore JSON parse error and keep generic message.
        }
        throw new Error(detail);
      }

      notify(t('auth.changePassword.notifications.success'), { type: 'success' });

      if (!isAdminChanging) {
        // Self-service: force re-login
        notify(t('auth.changePassword.notifications.relogin'), { type: 'info' });
        localStorage.removeItem('token');
        localStorage.removeItem('tokenType');
        localStorage.removeItem('user');
        window.location.href = '/login';
      } else {
        // Admin change: just close and refresh
        if (onSuccess) {
          onSuccess();
        }
        handleClose();
      }
    } catch (error: any) {
      console.error('Password change error:', error);
      const errorMessage = error?.message ||
                          t('auth.changePassword.notifications.failed');
      notify(errorMessage, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const dialogTitle = isAdminChanging
    ? t('auth.changePassword.titleAdmin', { name: userName || t('auth.defaultUser') })
    : t('auth.changePassword.titleSelf');

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {isAdminChanging && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('auth.changePassword.adminNotice', { name: userName || t('auth.defaultUser') })}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!isAdminChanging && (
              <TextField
                label={t('auth.changePassword.fields.oldPassword')}
                type="password"
                fullWidth
                value={formData.old_password}
                onChange={(e) => handleInputChange('old_password', e.target.value)}
                error={!!errors.old_password}
                helperText={errors.old_password}
                disabled={loading}
              />
            )}

            <TextField
              label={t('auth.changePassword.fields.newPassword')}
              type="password"
              fullWidth
              value={formData.new_password}
              onChange={(e) => handleInputChange('new_password', e.target.value)}
              error={!!errors.new_password}
              helperText={errors.new_password}
              disabled={loading}
            />

            <Box sx={{ bgcolor: '#f5f5f5', p: 1.5, borderRadius: 1 }}>
              <Typography variant="caption" color="textSecondary">
                {t('auth.changePassword.requirementsTitle')}
              </Typography>
              <Typography variant="caption" display="block">
                • {t('auth.changePassword.requirements.length')}
              </Typography>
              <Typography variant="caption" display="block">
                • {t('auth.changePassword.requirements.uppercase')}
              </Typography>
              <Typography variant="caption" display="block">
                • {t('auth.changePassword.requirements.lowercase')}
              </Typography>
              <Typography variant="caption" display="block">
                • {t('auth.changePassword.requirements.digit')}
              </Typography>
            </Box>

            <TextField
              label={t('auth.changePassword.fields.confirmPassword')}
              type="password"
              fullWidth
              value={formData.new_password_confirm}
              onChange={(e) => handleInputChange('new_password_confirm', e.target.value)}
              error={!!errors.new_password_confirm}
              helperText={errors.new_password_confirm}
              disabled={loading}
            />
          </Box>
        </form>
      </DialogContent>
      <DialogActions sx={{ pt: 0 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('auth.changePassword.actions.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {loading ? t('auth.changePassword.actions.submitting') : t('auth.changePassword.actions.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordDialog;
